import React, { useState, useEffect, useMemo } from "react";
import "../assets/css/customerCards.css";
import DashboardFooter from "../components/DashboardFooter";
import { NotificationManager } from "react-notifications";

const EMPTY_SALARY = {
  id: null,
  salary_period: "2026-07-01",
  employee_id: "",
  basic_salary: "",
  earned_salary: "",
  accomodation: "",
  earned_accomodation:"",
  reimbursments: "",
  leave_salary: "",
  unpaid_leave_salary: "",
  joining_incentive: "",
  overtime_amount: "",
  overtime_day: "",
  overtime_hours: "",
  bonus: "",
  public_holiday: "",
  pass_type: "",
  employee_cpf: "",
  loan: "",
  advance_salary: "",
  community_fund_cdac: "",
  withhold_income_tax: "",
  fine : "",
  others_1: "",
  others_2: "",
  others_3: "",
  others_4: "",
  others_5: "",
  gross_income: "",
  total_deductions: "",
  net_salary: "",
  employer_cpf: "",
  total_cpf_payable: "",
  payment_mode: "Bank Transfer",
  payment_date: "2026-07-31",
  pay_slip_issue_date: "",
};

// Fields that feed the gross income calculation
const EARNING_FIELDS = [
  "basic_salary",
  "earned_salary",
  "accomodation",
  "earned_accomodation",
  "reimbursments",
  "leave_salary",
  "unpaid_leave_salary",
  "joining_incentive",
  "overtime_amount",
  "bonus",
  "public_holiday",
  "others_1",
  "others_2",
  "others_3",
  "others_4",
  "others_5",
];

// Fields that feed the total deductions calculation
const DEDUCTION_FIELDS = [
  "employee_cpf",
  "loan",
  "advance_salary",
  "community_fund_cdac",
  "withhold_income_tax",
  "fine",
];

// Auto-calculated fields — rendered as read-only in the form
const READONLY_FIELDS = new Set(["gross_income", "total_deductions", "net_salary", "total_cpf_payable"]);

const PASS_TYPES = [
  "Singapore Citizen",
  "Permanent Resident",
  "Employment Pass",
  "S Pass",
  "Work Permit",
  "Dependant's Pass",
  "Other",
];

const FIELD_GROUPS = [
  {
    title: "Earnings",
    fields: [
      ["basic_salary", "Basic Salary"],
      ["earned_salary", "Earned Salary"],
      ["accomodation", "Accommodation"],
      ["earned_accomodation", "Earned Accomodation"],
      ["reimbursments", "Reimbursements"],
      ["leave_salary", "Leave Salary"],
      ["unpaid_leave_salary", "Unpaid Leave"],
      ["joining_incentive", "Joining Incentive"],
      ["overtime_amount", "Overtime Amount"],
      ["overtime_day", "Overtime Day (not counted in Gross Income)"],
      ["overtime_hours", "Overtime Hours (not counted in Gross Income)"],
      ["bonus", "Bonus"],
      ["public_holiday", "Public Holiday"],
    ],
  },
  {
    title: "Deductions",
    fields: [
      ["employee_cpf", "Employee CPF"],
      ["loan", "Loan"],
      ["advance_salary", "Advance Salary"],
      ["community_fund_cdac", "Community Fund (CDAC)"],
      ["withhold_income_tax", "Income Tax"],
      ["fine", "Fine"],
    ],
  },
  {
    title: "Others",
    fields: [
      ["others_1", "Others 1"],
      ["others_2", "Others 2"],
      ["others_3", "Others 3"],
      ["others_4", "Others 4"],
      ["others_5", "Others 5"],
    ],
  },
  {
    title: "CPF",
    fields: [
      ["employer_cpf", "Employer CPF"],
      ["total_cpf_payable", "Total CPF Payable (auto)"],
    ],
  },
  {
    title: "Totals (auto-calculated)",
    fields: [
      ["gross_income", "Gross Income"],
      ["total_deductions", "Total Deductions"],
      ["net_salary", "Net Salary"],
    ],
  },
];

const MONTHS = [
  { value: "", label: "All Months" },
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const ManageSalary = () => {
  const token = localStorage.getItem("token");

  const [employees, setEmployees] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [salary, setSalary] = useState(EMPTY_SALARY);

  const [filters, setFilters] = useState({
    employee_id: "",
    year: "",
    month: "",
  });

  const authHeaders = {
    Authorization: "Bearer " + token,
    "Content-Type": "application/json",
  };

  // ---------- Fetch employees ----------
  const fetchEmployees = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_HOST_API_URL}/app/employes-list/`,
        {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({'all': 1}),
        }
      );
      if (!res.ok) throw new Error("Failed to fetch employees");
      const data = await res.json();
      setEmployees(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      NotificationManager.error("Error fetching employees");
    }
  };

  // ---------- Fetch salary list ----------
  const fetchSalaries = async () => {
    setLoadingList(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_HOST_API_URL}/admin-user/salary-data/`,
        {
          method: "GET",
          headers: authHeaders,
        }
      );
      if (!res.ok) throw new Error("Failed to fetch salary records");
      const data = await res.json();
      // console.log("Fetched salaries:", data);
      setSalaries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      NotificationManager.error("Error fetching salary records");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchSalaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const employeeMap = useMemo(() => {
    const map = {};
    employees.forEach((emp) => {
      map[emp.id] = emp.full_name || emp.name || emp.employee_name || emp.username;
    });
    return map;
  }, [employees]);

  const getEmployeeName = (id) => employeeMap[id] || `Employee #${id}`;

  // ---------- Filtering (client side) ----------
  const filteredSalaries = useMemo(() => {
  return salaries.filter((s) => {
    if (filters.employee_id && String(s.employee) !== String(filters.employee_id)) {
      return false;
    }
    if (s.salary_period) {
      const [y, m] = s.salary_period.split("-");
      if (filters.year && y !== filters.year) return false;
      if (filters.month && m !== filters.month) return false;
    } else if (filters.year || filters.month) {
      return false;
    }
    return true;
  });
}, [salaries, filters]);

  const years = useMemo(() => {
    const set = new Set();
    salaries.forEach((s) => {
      if (s.salary_period) set.add(s.salary_period.split("-")[0]);
    });
    return Array.from(set).sort((a, b) => b - a);
  }, [salaries]);

  // ---------- Auto-calculate Gross Income / Total Deductions / Net Salary / Total CPF ----------
  useEffect(() => {
    const sum = (fields) =>
      fields.reduce((acc, f) => acc + (parseFloat(salary[f]) || 0), 0);

    const gross = sum(EARNING_FIELDS);
    const totalDeductions = sum(DEDUCTION_FIELDS);
    const net = gross - totalDeductions;
    const totalCpf = (parseFloat(salary.employee_cpf) || 0) + (parseFloat(salary.employer_cpf) || 0);

    const grossStr = gross.toFixed(2);
    const deductionsStr = totalDeductions.toFixed(2);
    const netStr = net.toFixed(2);
    const cpfStr = totalCpf.toFixed(2);

    if (
      salary.gross_income !== grossStr ||
      salary.total_deductions !== deductionsStr ||
      salary.net_salary !== netStr ||
      salary.total_cpf_payable !== cpfStr
    ) {
      setSalary((prev) => ({
        ...prev,
        gross_income: grossStr,
        total_deductions: deductionsStr,
        net_salary: netStr,
        total_cpf_payable: cpfStr,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // salary.basic_salary,
    salary.earned_salary,
    // salary.accomodation,
    salary.earned_accomodation,
    salary.reimbursments,
    salary.leave_salary,
    salary.unpaid_leave_salary,
    salary.joining_incentive,
    salary.overtime_amount,
    salary.bonus,
    salary.public_holiday,
    salary.others_1,
    salary.others_2,
    salary.others_3,
    salary.others_4,
    salary.others_5,
    salary.employee_cpf,
    salary.loan,
    salary.advance_salary,
    salary.community_fund_cdac,
    salary.withhold_income_tax,
    salary.fine,
    salary.employer_cpf,
  ]);

  // ---------- Form handlers ----------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setSalary((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const openAddForm = () => {
    setSalary(EMPTY_SALARY);
    setShowForm(true);
  };

  // const openEditForm = (record) => {
  //   setSalary({ ...EMPTY_SALARY, ...record });
  //   setShowForm(true);
  //   window.scrollTo({ top: 0, behavior: "smooth" });
  // };

  const openEditForm = (record) => {
  setSalary({
    ...EMPTY_SALARY,
    ...record,
    employee_id: record.employee ?? record.employee_id ?? "",
  });
  setShowForm(true);
  window.scrollTo({ top: 0, behavior: "smooth" });
};

  const closeForm = () => {
    setShowForm(false);
    setSalary(EMPTY_SALARY);
  };

  const toNumberOrZero = (val) => (val === "" || val === null || val === undefined ? 0 : Number(val));

  
  // ---------- Download salary slip (PDF) ----------
  const downloadSlip = async (date, employeeId, recordId) => {
    if (!date || !employeeId) {
      NotificationManager.error("Missing date or employee for this record");
      return;
    }
 
    setDownloadingId(recordId);
    try {
      const formData = new FormData();
      formData.append("date", date);
      formData.append("employee_id", employeeId);
 
      const response = await fetch(
        `${process.env.REACT_APP_HOST_API_URL}/admin-user/download-employee-salary-slip/`,
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + token,
            // Do NOT set Content-Type here — the browser sets the correct
            // multipart/form-data boundary automatically for FormData.
          },
          body: formData,
        }
      );
 
      if (!response.ok) throw new Error("Failed to download slip");
 
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
 
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `salary-slip-${date}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error(error);
      NotificationManager.error("Failed to download the slip");
    } finally {
      setDownloadingId(null);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!salary.employee_id) {
      NotificationManager.error("Please select an employee");
      return;
    }

    const payload = {
      salary_period: salary.salary_period,
      employee: Number(salary.employee_id),
      basic_salary: toNumberOrZero(salary.basic_salary),
      earned_salary:toNumberOrZero(salary.earned_salary),
      accomodation: toNumberOrZero(salary.accomodation),
      earned_accomodation: toNumberOrZero(salary.earned_accomodation),
      reimbursments: toNumberOrZero(salary.reimbursments),
      leave_salary: toNumberOrZero(salary.leave_salary),
      unpaid_leave_salary: toNumberOrZero(salary.unpaid_leave_salary),
      joining_incentive: toNumberOrZero(salary.joining_incentive),
      overtime_amount: toNumberOrZero(salary.overtime_amount),
      overtime_day: toNumberOrZero(salary.overtime_day),
      overtime_hours: toNumberOrZero(salary.overtime_hours),
      bonus: toNumberOrZero(salary.bonus),
      public_holiday: toNumberOrZero(salary.public_holiday),
      pass_type: salary.pass_type || null,
      employee_cpf: toNumberOrZero(salary.employee_cpf),
      loan: toNumberOrZero(salary.loan),
      advance_salary: toNumberOrZero(salary.advance_salary),
      community_fund_cdac: toNumberOrZero(salary.community_fund_cdac),
      withhold_income_tax: toNumberOrZero(salary.withhold_income_tax),
      fine :toNumberOrZero(salary.fine),
      others_1: toNumberOrZero(salary.others_1),
      others_2: toNumberOrZero(salary.others_2),
      others_3: toNumberOrZero(salary.others_3),
      others_4: toNumberOrZero(salary.others_4),
      others_5: toNumberOrZero(salary.others_5),
      gross_income: toNumberOrZero(salary.gross_income),
      total_deductions: toNumberOrZero(salary.total_deductions),
      net_salary: toNumberOrZero(salary.net_salary),
      employer_cpf: toNumberOrZero(salary.employer_cpf),
      total_cpf_payable: toNumberOrZero(salary.total_cpf_payable),
      payment_mode: salary.payment_mode,
      payment_date: salary.payment_date,
      pay_slip_issue_date: salary.pay_slip_issue_date || null,
    };

    const isEditing = Boolean(salary.id);
    const url = isEditing
      ? `${process.env.REACT_APP_HOST_API_URL}/admin-user/salary-data/${salary.id}/`
      : `${process.env.REACT_APP_HOST_API_URL}/admin-user/salary-data/`;
    const method = isEditing ? "PUT" : "POST";

    setSaving(true);
    try {
      const response = await fetch(url, {
        method,
        headers: authHeaders,
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to save salary");

      const data = await response.json();
      const savedRecord = data.data || data;

      // Update list in place without a full reload
      setSalaries((prev) => {
        if (isEditing) {
          return prev.map((s) => (s.id === savedRecord.id ? savedRecord : s));
        }
        return [savedRecord, ...prev];
      });

      NotificationManager.success(
        isEditing ? "Salary updated successfully" : "Salary added successfully"
      );

      // Stay on the page. Keep all the entered amounts so the user can
      // just switch the employee and save again, instead of re-typing
      // everything. Only the employee + editing id are reset.
      setSalary((prev) => ({
        ...prev,
        id: null,
        employee_id: "",
      }));
    } catch (error) {
      console.error(error);
      NotificationManager.error(
        isEditing ? "Error updating salary" : "Error saving salary"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="d-flex flex-column min-vh-100 pb-2 px-3">
      <div className="flex-grow-1">
        <div className="container-fluid pb-2 px-2 px-md-4" style={{ paddingTop: "20px" }}>
          {/* Header */}
          <div
            className="row mb-4 bg-white shadow-sm border-bottom sticky-top py-3 px-md-3 px-2 rounded align-items-center"
            style={{
              zIndex: 1050,
              background: "linear-gradient(135deg, #0564bf, #89CFF0)",
            }}
          >
            <div className="col-12 col-md-8">
              <h3 className="text-light mb-1">Manage Salary</h3>
              <p className="text-light mb-0">
                Create and manage employee salary records
              </p>
            </div>
            <div className="col-12 col-md-4 text-md-end mt-2 mt-md-0">
              <button
                className="btn btn-light fw-semibold px-4"
                onClick={showForm ? closeForm : openAddForm}
              >
                {showForm ? "Close Form" : "+ Add New Salary"}
              </button>
            </div>
          </div>

          {/* Add / Edit Form */}
          {showForm && (
            <div className="row mb-4">
              <div className="col-12">
                <div className="border rounded p-3 bg-white shadow-sm">
                  <h4 className="text-center mb-4" style={{ color: "#0564bf" }}>
                    {salary.id ? "Edit Salary" : "Add Salary"}
                  </h4>

                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label small text-muted mb-1">Employee</label>
                      <select
                        name="employee_id"
                        value={salary.employee_id}
                        onChange={handleChange}
                        className="form-control"
                      >
                        <option value="">Select Employee</option>
                        {employees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.full_name || emp.name || emp.employee_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label small text-muted mb-1">Salary Period</label>
                      <input
                        type="date"
                        name="salary_period"
                        value={salary.salary_period || ""}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label small text-muted mb-1">Pass Type</label>
                      <select
                        name="pass_type"
                        value={salary.pass_type || ""}
                        onChange={handleChange}
                        className="form-control"
                      >
                        <option value="">Select Pass Type</option>
                        {PASS_TYPES.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>

                    {FIELD_GROUPS.map((group) => (
                      <React.Fragment key={group.title}>
                        <div className="col-12 mt-3">
                          <h6 className="text-secondary border-bottom pb-1">{group.title}</h6>
                        </div>
                        {group.fields.map(([name, label]) => {
                          const isReadOnly = READONLY_FIELDS.has(name);
                          return (
                            <div className="col-12 col-md-6 col-lg-4" key={name}>
                              <label className="form-label small text-muted mb-1">{label}</label>
                              <input
                                type="number"
                                step="0.01"
                                name={name}
                                placeholder={label}
                                value={salary[name] ?? ""}
                                onChange={handleChange}
                                readOnly={isReadOnly}
                                className={`form-control ${isReadOnly ? "bg-light fw-semibold" : ""}`}
                              />
                            </div>
                          );
                        })}
                      </React.Fragment>
                    ))}

                    <div className="col-12 mt-3">
                      <h6 className="text-secondary border-bottom pb-1">Payment Information</h6>
                    </div>

                    <div className="col-12 col-md-4">
                      <label className="form-label small text-muted mb-1">Payment Mode</label>
                      <select
                        name="payment_mode"
                        value={salary.payment_mode}
                        onChange={handleChange}
                        className="form-control"
                      >
                        <option>Bank Transfer</option>
                        <option>Cash</option>
                        <option>Cheque</option>
                      </select>
                    </div>

                    <div className="col-12 col-md-4">
                      <label className="form-label small text-muted mb-1">Payment Date</label>
                      <input
                        type="date"
                        name="payment_date"
                        value={salary.payment_date || ""}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>

                    <div className="col-12 col-md-4">
                      <label className="form-label small text-muted mb-1">Pay Slip Issue Date</label>
                      <input
                        type="date"
                        name="pay_slip_issue_date"
                        value={salary.pay_slip_issue_date || ""}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div className="d-flex justify-content-center gap-3 mt-4">
                    <button
                      onClick={handleSubmit}
                      className="btn btn-outline-primary px-5"
                      disabled={saving}
                    >
                      {saving ? "Saving..." : salary.id ? "Update Salary" : "Save Salary"}
                    </button>
                    <button
                      onClick={closeForm}
                      className="btn btn-outline-secondary px-5"
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="row mb-3 ">
            <div className="col-12">
              <div className="border rounded p-3  shadow-sm" style={{
          background: "linear-gradient(135deg, #0564bf, #89CFF0",
        }}>
                <div className="row g-3 align-items-end">
                  <div className="col-12 col-md-4">
                    <label className="form-label small text-white mb-1">Employee</label>
                    <select
                      name="employee_id"
                      value={filters.employee_id}
                      onChange={handleFilterChange}
                      className="form-control"
                    >
                      <option value="">All Employees</option>
                      {employees.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.full_name || emp.name || emp.employee_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-6 col-md-3">
                    <label className="form-label small text-white mb-1">Year</label>
                    <select
                      name="year"
                      value={filters.year}
                      onChange={handleFilterChange}
                      className="form-control"
                    >
                      <option value="">All Years</option>
                      {years.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-6 col-md-3">
                    <label className="form-label small text-white mb-1">Month</label>
                    <select
                      name="month"
                      value={filters.month}
                      onChange={handleFilterChange}
                      className="form-control"
                    >
                      {MONTHS.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12 col-md-2">
                    <button
                      className="btn btn-white w-100"
                      onClick={() => setFilters({ employee_id: "", year: "", month: "" })}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Salary List */}
          <div className="row">
            <div className="col-12">
              <div className="border rounded p-3 bg-white shadow-sm">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0" style={{ color: "#0564bf" }}>
                    Salary Records
                  </h5>
                  <span className="text-muted small">
                    {filteredSalaries.length} record{filteredSalaries.length !== 1 ? "s" : ""}
                  </span>
                </div>

                {loadingList ? (
                  <p className="text-center text-muted my-4">Loading...</p>
                ) : filteredSalaries.length === 0 ? (
                  <p className="text-center text-muted my-4">No salary records found.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle">
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th>Pass Type</th>
                          <th>Salary Period</th>
                          <th>Basic Salary</th>
                          <th>Earned Salary</th>
                          <th>Gross Income</th>
                          <th>Total Deductions</th>
                          <th>Net Salary</th>
                          <th>Total CPF</th>
                          <th>Payment Mode</th>
                          <th>Payment Date</th>
                          <th className="text-end">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSalaries.map((s) => (
                          <tr key={s.id}>
                            {/* <td>{getEmployeeName(s.employee_id)}</td> */}
                            <td>{s.employee_name || "-"}</td>
                            <td>{s.pass_type || "-"}</td>
                            <td>{s.salary_period}</td>
                            <td>{s.basic_salary}</td>
                            <td>{s.earned_salary}</td>
                            <td>{s.gross_income}</td>
                            <td>{s.total_deductions}</td>
                            <td className="fw-semibold">{s.net_salary}</td>
                            <td>{s.total_cpf_payable}</td>
                            <td>{s.payment_mode}</td>
                            <td>{s.payment_date}</td>
                            <td className="text-end">
                               <div className="d-flex justify-content-end gap-2">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => openEditForm(s)}
                                >
                                  Edit
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-success text-nowrap"
                                  onClick={() =>
                                    downloadSlip(s.salary_period, s.employee, s.id)
                                  }
                                  disabled={downloadingId === s.id}
                                >
                                  {downloadingId === s.id ? "Downloading..." : "Download"}
                                </button>
                              </div>
                              
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <DashboardFooter />
    </div>
  );
};

export default ManageSalary;